/**
 * Age groups used to scope decks/cards to a learner's cognitive stage.
 *
 * Roughly aligned with school curricula breakpoints (3-5 / 6-8 / 9-12 / teen)
 * and adult tiers (adult / professional / academic). These map onto CEFR
 * levels only loosely — a teen can be at C1, an adult at A1.
 */

import type { Localized } from "./locale";

export type AgeGroupId =
  | "age_3_5"
  | "age_6_8"
  | "age_9_12"
  | "teen"
  | "adult"
  | "professional"
  | "academic";

export type AgeGroupRef = {
  id: AgeGroupId;
  name: Localized;
  hint?: Localized;
  /** Tailwind tone — kept as a soft chip background everywhere. */
  tone: "pink" | "amber" | "emerald" | "sky" | "slate" | "indigo" | "violet";
};

export const AGE_GROUPS: AgeGroupRef[] = [
  {
    id: "age_3_5",
    name: { ru: "3–5 лет", en: "3–5 years", es: "3–5 años", fr: "3–5 ans", de: "3–5 Jahre", it: "3–5 anni", zh: "3–5岁", ja: "3〜5歳" },
    hint: { ru: "Дошкольный возраст", en: "Preschool", es: "Preescolar", fr: "Maternelle", de: "Vorschule", it: "Prescolare", zh: "学前", ja: "未就学児" },
    tone: "pink",
  },
  {
    id: "age_6_8",
    name: { ru: "6–8 лет", en: "6–8 years", es: "6–8 años", fr: "6–8 ans", de: "6–8 Jahre", it: "6–8 anni", zh: "6–8岁", ja: "6〜8歳" },
    hint: { ru: "Начальная школа, младшие", en: "Early primary school", es: "Primaria temprana", fr: "Début du primaire", de: "Frühe Grundschule", it: "Primaria iniziale", zh: "小学低年级", ja: "小学校低学年" },
    tone: "amber",
  },
  {
    id: "age_9_12",
    name: { ru: "9–12 лет", en: "9–12 years", es: "9–12 años", fr: "9–12 ans", de: "9–12 Jahre", it: "9–12 anni", zh: "9–12岁", ja: "9〜12歳" },
    hint: { ru: "Начальная школа, старшие", en: "Late primary school", es: "Primaria avanzada", fr: "Fin du primaire", de: "Späte Grundschule", it: "Primaria avanzata", zh: "小学高年级", ja: "小学校高学年" },
    tone: "emerald",
  },
  {
    id: "teen",
    name: { ru: "Подростки", en: "Teens", es: "Adolescentes", fr: "Adolescents", de: "Jugendliche", it: "Adolescenti", zh: "青少年", ja: "ティーン" },
    hint: { ru: "13–17", en: "13–17", es: "13–17", fr: "13–17", de: "13–17", it: "13–17", zh: "13–17", ja: "13〜17歳" },
    tone: "sky",
  },
  {
    id: "adult",
    name: { ru: "Взрослые", en: "Adults", es: "Adultos", fr: "Adultes", de: "Erwachsene", it: "Adulti", zh: "成人", ja: "成人" },
    tone: "slate",
  },
  {
    id: "professional",
    name: { ru: "Профи", en: "Professional", es: "Profesional", fr: "Professionnel", de: "Profi", it: "Professionale", zh: "专业人士", ja: "プロ" },
    hint: { ru: "Профессиональный уровень", en: "Professional level", es: "Nivel profesional", fr: "Niveau professionnel", de: "Berufliches Niveau", it: "Livello professionale", zh: "专业水平", ja: "プロ水準" },
    tone: "indigo",
  },
  {
    id: "academic",
    name: { ru: "Академический", en: "Academic", es: "Académico", fr: "Académique", de: "Akademisch", it: "Accademico", zh: "学术", ja: "学術" },
    hint: { ru: "Университет и наука", en: "University and research", es: "Universidad e investigación", fr: "Université et recherche", de: "Universität und Forschung", it: "Università e ricerca", zh: "大学与科研", ja: "大学・研究" },
    tone: "violet",
  },
];

export const AGE_GROUP_BY_ID: Record<AgeGroupId, AgeGroupRef> = Object.fromEntries(
  AGE_GROUPS.map((g) => [g.id, g]),
) as Record<AgeGroupId, AgeGroupRef>;

export function isAgeGroupId(value: unknown): value is AgeGroupId {
  return typeof value === "string" && value in AGE_GROUP_BY_ID;
}
